import { z } from "zod";

// 1.D.4 API-client tyypit ja zod-skeemat.
//
// Skeemat ovat tarkoituksella loose (.loose()) jotta API:n kasvavat
// kentat eivat hajota web-clientiä — UI lukee vain ne kentat joita se
// nayttaa, muut menevat lapi.

export const ProjectAccessSchema = z.enum(["viewer", "editor", "owner"]);
export type ProjectAccess = z.infer<typeof ProjectAccessSchema>;

export const UserMembershipSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    access: ProjectAccessSchema,
  })
  .loose();
export type UserMembership = z.infer<typeof UserMembershipSchema>;

export const MeUserSchema = z
  .object({
    id: z.string(),
    email: z.string(),
    name: z.string(),
    roles: z.array(z.string()).default([]),
    projects: z.array(UserMembershipSchema).default([]),
    // 2.5.D.1 laajennukset
    agePublicKey: z.string().nullable().optional(),
    registeredAt: z.string().nullable().optional(),
    lastSignedInAt: z.string().nullable().optional(),
  })
  .loose();
export type MeUser = z.infer<typeof MeUserSchema>;

export const MeResponseSchema = z
  .object({
    user: MeUserSchema,
  })
  .loose();
export type MeResponse = z.infer<typeof MeResponseSchema>;

// 2.5.E.1: daemon-machine / config / disks -tyypit web-clientille.
export const DaemonMachineSchema = z
  .object({
    id: z.string(),
    userId: z.string(),
    label: z.string().nullable(),
    hostname: z.string().nullable(),
    agentVersion: z.string().nullable(),
    osPlatform: z.string().nullable(),
    osVersion: z.string().nullable(),
    registeredAt: z.string(),
    lastSeenAt: z.string(),
    status: z.enum(["online", "unknown", "offline"]),
    sourceRootsCount: z.number().int(),
    disksCount: z.number().int(),
  })
  .loose();
export type DaemonMachine = z.infer<typeof DaemonMachineSchema>;

export const DaemonMachinesResponseSchema = z
  .object({ machines: z.array(DaemonMachineSchema).default([]) })
  .loose();
export type DaemonMachinesResponse = z.infer<typeof DaemonMachinesResponseSchema>;

export const DaemonConfigSchema = z
  .object({
    sourceRoots: z.array(z.string()).default([]),
    pacingMode: z.enum(["polite", "balanced", "fast"]),
    additionalOptions: z.record(z.string(), z.unknown()).default({}),
  })
  .loose();
export type DaemonConfig = z.infer<typeof DaemonConfigSchema>;

export const DaemonConfigResponseSchema = z
  .object({
    machineId: z.string(),
    config: DaemonConfigSchema,
    configUpdatedAt: z.string(),
    configUpdatedBy: z.string().nullable(),
  })
  .loose();
export type DaemonConfigResponse = z.infer<typeof DaemonConfigResponseSchema>;

export const DaemonDiskSchema = z
  .object({
    deviceId: z.string(),
    mountPath: z.string(),
    label: z.string().nullable(),
    sizeBytes: z.number().nullable(),
    availableBytes: z.number().nullable(),
    isUsb: z.boolean(),
    isInternal: z.boolean(),
    lastSeenAt: z.string(),
  })
  .loose();
export type DaemonDisk = z.infer<typeof DaemonDiskSchema>;

export const DaemonDisksResponseSchema = z
  .object({
    machineId: z.string(),
    disks: z.array(DaemonDiskSchema).default([]),
  })
  .loose();
export type DaemonDisksResponse = z.infer<typeof DaemonDisksResponseSchema>;

// 3.B.1.4: snapshot-diff vastaus (daemon-inbox).
export const SnapshotDiffSummarySchema = z.object({
  addedCount: z.number(),
  modifiedCount: z.number(),
  removedCount: z.number(),
  addedBytes: z.number(),
  modifiedBytes: z.number(),
  removedBytes: z.number(),
});

export const SnapshotDiffEntrySchema = z.object({
  path: z.string(),
  size: z.number(),
});

export const SnapshotDiffModifiedEntrySchema = z.object({
  path: z.string(),
  sizeOld: z.number(),
  sizeNew: z.number(),
  mtimeOld: z.string().nullable(),
  mtimeNew: z.string().nullable(),
});

export const SnapshotDiffResponseSchema = z.object({
  added: z.array(SnapshotDiffEntrySchema),
  modified: z.array(SnapshotDiffModifiedEntrySchema),
  removed: z.array(SnapshotDiffEntrySchema),
  summary: SnapshotDiffSummarySchema,
});
export type SnapshotDiffResponse = z.infer<typeof SnapshotDiffResponseSchema>;
export type SnapshotDiffEntry = z.infer<typeof SnapshotDiffEntrySchema>;
export type SnapshotDiffModifiedEntry = z.infer<typeof SnapshotDiffModifiedEntrySchema>;
export type SnapshotDiffSummary = z.infer<typeof SnapshotDiffSummarySchema>;

// 3.B.1.5: asset-versions vastaus.
export const AssetVersionsResponseSchema = z
  .object({
    current: z.object({
      contentHash: z.string(),
      sizeBytes: z.union([z.number(), z.string()]).nullable(),
      capturedAt: z.string().nullable(),
    }),
    versions: z.array(
      z.object({
        snapshotId: z.string(),
        snapshotTime: z.string(),
        contentHash: z.string(),
        sizeBytes: z.union([z.number(), z.string()]).nullable(),
        isIdentical: z.boolean(),
      })
    ),
  })
  .loose();
export type AssetVersionsResponse = z.infer<typeof AssetVersionsResponseSchema>;

// 3.A.7.4: inbox-pyynnöt web-puolelta.
export const InboxRequestCreatedSchema = z
  .object({ id: z.string().uuid(), status: z.literal("pending") })
  .loose();
export type InboxRequestCreated = z.infer<typeof InboxRequestCreatedSchema>;

export const InboxStatusSchema = z
  .object({
    id: z.string().uuid(),
    status: z.enum([
      "pending",
      "in_progress",
      "completed",
      "failed",
      "cancelled",
      "timeout",
    ]),
    result: z.unknown().nullable(),
    error: z.string().nullable(),
    createdAt: z.string(),
    updatedAt: z.string(),
  })
  .loose();
export type InboxStatus = z.infer<typeof InboxStatusSchema>;

export const AssetSchema = z
  .object({
    id: z.string(),
    ownerId: z.string(),
    projectId: z.string().nullable(),
    currentPath: z.string(),
    currentTier: z.string(),
    currentMetaStatus: z.string(),
    sizeBytes: z.union([z.number(), z.string()]).nullable().optional(),
    createdAt: z.string(),
    // 2.G.1 — MediaInfo:n metadata-kentat
    capturedAt: z.string().nullable().optional(),
    cameraSerial: z.string().nullable().optional(),
    make: z.string().nullable().optional(),
    model: z.string().nullable().optional(),
    lens: z.string().nullable().optional(),
    focalLength: z.number().nullable().optional(),
    fNumber: z.number().nullable().optional(),
    iso: z.number().nullable().optional(),
    exposureTime: z.number().nullable().optional(),
    format: z.string().nullable().optional(),
    encodedApplication: z.string().nullable().optional(),
    width: z.number().nullable().optional(),
    height: z.number().nullable().optional(),
    duration: z.number().nullable().optional(),
    videoCodec: z.string().nullable().optional(),
    audioCodec: z.string().nullable().optional(),
    audioSampleRate: z.number().nullable().optional(),
    audioChannels: z.number().nullable().optional(),
    rating: z.number().optional(),
    flag: z.string().optional(),
    colorLabel: z.string().optional(),
  })
  .loose();
export type Asset = z.infer<typeof AssetSchema>;

export const AssetListResponseSchema = z
  .object({
    assets: z.array(AssetSchema),
    nextCursor: z.string().nullable(),
    limit: z.number(),
  })
  .loose();
export type AssetListResponse = z.infer<typeof AssetListResponseSchema>;

// GET /folders kansio-view-mode -vastaus
export const FolderEntrySchema = z.object({
  name: z.string(),
  assetCount: z.number(),
  totalBytes: z.union([z.number(), z.string()]),
});
export const FoldersResponseSchema = z
  .object({
    path: z.string(),
    folders: z.array(FolderEntrySchema),
    files: z.array(AssetSchema),
  })
  .loose();
export type FolderEntry = z.infer<typeof FolderEntrySchema>;
export type FoldersResponse = z.infer<typeof FoldersResponseSchema>;

export const AssetMetadataSchema = z
  .object({
    id: z.string(),
    fieldKey: z.string(),
    fieldLabel: z.string(),
    fieldType: z.string(),
    value: z.unknown(),
    setBy: z.string(),
  })
  .loose();
export type AssetMetadata = z.infer<typeof AssetMetadataSchema>;

export const AssetLocationSchema = z
  .object({
    snapshotId: z.string(),
    kopiaSnapshotId: z.string(),
    startedAt: z.string(),
    repositoryId: z.string(),
    repositoryKind: z.string(),
    repositoryLocation: z.string(),
    pathInSnapshot: z.string(),
  })
  .loose();
export type AssetLocation = z.infer<typeof AssetLocationSchema>;

export const AssetDetailResponseSchema = z
  .object({
    asset: AssetSchema,
    metadata: z.array(AssetMetadataSchema),
    tags: z.array(z.string()),
    versions: z.array(z.unknown()),
    locations: z.array(AssetLocationSchema),
  })
  .loose();
export type AssetDetailResponse = z.infer<typeof AssetDetailResponseSchema>;

export const ProjectSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    ownerId: z.string(),
    createdAt: z.string(),
    lifecycleState: z.enum(["active", "archived"]).optional(),
    status: z.enum(["scout", "active", "delivered", "archived"]).optional(),
    forkedFromProjectId: z.string().nullable().optional(),
    // 3.A.5: discovered_via + sidecar_path + description (vaihe 3.A laajennukset)
    discoveredVia: z.enum(["manual", "sidecar", "convention", "vault"]).optional(),
    sidecarPath: z.string().nullable().optional(),
    description: z.string().nullable().optional(),
  })
  .loose();
export type Project = z.infer<typeof ProjectSchema>;

export const ProjectListResponseSchema = z
  .object({
    projects: z.array(ProjectSchema),
  })
  .loose();
export type ProjectListResponse = z.infer<typeof ProjectListResponseSchema>;

export const ProjectDetailResponseSchema = z
  .object({
    project: ProjectSchema,
    members: z.array(
      z
        .object({
          userId: z.string(),
          access: ProjectAccessSchema,
          name: z.string().nullable().optional(),
          email: z.string().nullable().optional(),
        })
        .loose()
    ),
    access: ProjectAccessSchema,
  })
  .loose();
export type ProjectDetailResponse = z.infer<typeof ProjectDetailResponseSchema>;

export const CreateProjectResponseSchema = z
  .object({
    project: ProjectSchema,
  })
  .loose();
export type CreateProjectResponse = z.infer<typeof CreateProjectResponseSchema>;

export const OnboardResponseSchema = z
  .object({
    alreadyOnboarded: z.boolean(),
    role: z.string(),
  })
  .loose();
export type OnboardResponse = z.infer<typeof OnboardResponseSchema>;

export const AssetListQuerySchema = z.object({
  projectId: z.string().optional(),
  tier: z.string().optional(),
  metaStatus: z.string().optional(),
  tags: z.string().optional(),
  cursor: z.string().optional(),
  limit: z.number().int().min(1).max(200).optional(),
  // 2.E.2-laajennukset (puuttuivat aiemmin client.ts:n listAssets-poiminnasta)
  q: z.string().optional(),
  ratingMin: z.string().optional(),
  ratingMax: z.string().optional(),
  flag: z.string().optional(),
  colorLabel: z.string().optional(),
  capturedFrom: z.string().optional(),
  capturedTo: z.string().optional(),
  // Post-3.B path-prefix-filter
  path: z.string().optional(),
});
export type AssetListQuery = z.infer<typeof AssetListQuerySchema>;

// Vaihe 4.B.3.UM — invite-only kayttajahallinta
export const UserInviteRoleSchema = z.enum(["user", "admin"]);
export type UserInviteRole = z.infer<typeof UserInviteRoleSchema>;

export const CreateInviteResponseSchema = z
  .object({
    inviteId: z.string(),
    inviteUrl: z.string(),
    expiresAt: z.string(),
  })
  .loose();
export type CreateInviteResponse = z.infer<typeof CreateInviteResponseSchema>;

export const InviteSchema = z
  .object({
    id: z.string(),
    email: z.string(),
    role: z.string(),
    expiresAt: z.string(),
    acceptedAt: z.string().nullable(),
    createdAt: z.string(),
  })
  .loose();
export type Invite = z.infer<typeof InviteSchema>;

export const ListInvitesResponseSchema = z
  .object({
    invites: z.array(InviteSchema).default([]),
  })
  .loose();
export type ListInvitesResponse = z.infer<typeof ListInvitesResponseSchema>;

export const AcceptInviteResponseSchema = z
  .object({
    user: z
      .object({
        id: z.string(),
        email: z.string(),
        name: z.string(),
      })
      .loose(),
  })
  .loose();
export type AcceptInviteResponse = z.infer<typeof AcceptInviteResponseSchema>;

// UM.6.A.3 — admin user list view-scope-pickerille
export const AdminUserSchema = z
  .object({
    id: z.string(),
    email: z.string(),
    name: z.string(),
  })
  .loose();
export type AdminUser = z.infer<typeof AdminUserSchema>;

export const AdminUsersResponseSchema = z
  .object({
    users: z.array(AdminUserSchema).default([]),
  })
  .loose();
export type AdminUsersResponse = z.infer<typeof AdminUsersResponseSchema>;

// E1.3 (ADR-016 V1++) — per-user S3-cred-admin

export const UserCredentialStatusSchema = z.object({
  createdAt: z.string(),
  rotatedAt: z.string().nullable(),
  hetznerKeyName: z.string().nullable().default(null),
  s3AccessKeyId: z.string().default(""),
  // ADR-020: per-user Scaleway-provisiointi-meta (optional koska
  // Hetzner-legacy-rivit eivät sisällä näitä)
  scalewayProjectId: z.string().nullable().default(null),
  scalewayBucketName: z.string().nullable().default(null),
  provisionedAt: z.string().nullable().default(null),
});
export type UserCredentialStatus = z.infer<typeof UserCredentialStatusSchema>;

export const PoolEntryStatusSchema = z.object({
  id: z.string(),
  s3AccessKeyId: z.string(),
  name: z.string().nullable().default(null),
  addedAt: z.string(),
});
export type PoolEntryStatus = z.infer<typeof PoolEntryStatusSchema>;

export const ListUserCredentialsResponseSchema = z.object({
  users: z.record(z.string(), UserCredentialStatusSchema).default({}),
  pool: z
    .object({
      available: z.number(),
      entries: z.array(PoolEntryStatusSchema).default([]),
    })
    .default({ available: 0, entries: [] }),
  hetznerConsoleUrl: z.string().default("https://console.hetzner.com"),
});
export type ListUserCredentialsResponse = z.infer<typeof ListUserCredentialsResponseSchema>;

export const SetUserCredentialResponseSchema = z.object({
  ok: z.boolean(),
  email: z.string(),
  createdAt: z.string(),
  rotatedAt: z.string().nullable(),
  hetznerKeyName: z.string().nullable().default(null),
  action: z.enum(["created", "rotated"]),
  source: z.enum(["explicit", "pool"]).default("explicit"),
  poolRemaining: z.number().default(0),
});
export type SetUserCredentialResponse = z.infer<typeof SetUserCredentialResponseSchema>;

export const AddPoolCredentialResponseSchema = z.object({
  ok: z.boolean(),
  id: z.string(),
  name: z.string().nullable().default(null),
  addedAt: z.string(),
  poolSize: z.number(),
});
export type AddPoolCredentialResponse = z.infer<typeof AddPoolCredentialResponseSchema>;

// ADR-020 — POST /admin/users/:email/provision-scaleway -vastaus
export const ProvisionScalewayResponseSchema = z.object({
  ok: z.boolean(),
  email: z.string(),
  projectId: z.string(),
  bucketName: z.string(),
  endpoint: z.string(),
  region: z.string(),
  provisionedAt: z.string(),
  action: z.enum(["provisioned", "rotated"]),
});
export type ProvisionScalewayResponse = z.infer<typeof ProvisionScalewayResponseSchema>;

// E1.3.c — käyttäjä-self credentials-status
// z.union (ei discriminated) koska "provisioned: false" toistuu kahdessa
// haarassa → discriminatedUnion ei toimi. union käy ne läpi yksitellen.
export const MyCredentialsResponseSchema = z.union([
  z.object({
    configured: z.literal(true),
    provisioned: z.literal(true),
    hetznerKeyName: z.string().nullable(),
    s3AccessKeyId: z.string(),
    createdAt: z.string(),
    rotatedAt: z.string().nullable(),
    hasPassphrase: z.boolean(),
    // ADR-020 — per-user Scaleway-meta (null jos Hetzner-legacy)
    scalewayProjectId: z.string().nullable().default(null),
    scalewayBucketName: z.string().nullable().default(null),
    scalewayEndpoint: z.string().nullable().default(null),
    scalewayRegion: z.string().nullable().default(null),
    provisionedAt: z.string().nullable().default(null),
  }),
  z.object({
    configured: z.literal(true),
    provisioned: z.literal(false),
    message: z.string(),
  }),
  z.object({
    configured: z.literal(false),
    mode: z.literal("shared_v1"),
    provisioned: z.literal(false).default(false),
  }),
]);
export type MyCredentialsResponse = z.infer<typeof MyCredentialsResponseSchema>;
